/**
 * Number Input with Commas Renderer
 * Automatically formats numbers with commas as the user types
 * Stores the numeric value (not the formatted string) in form data
 */
import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { FieldRenderer } from '@/MyCode/core';

export const renderNumberInputWithCommas: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    renderIcon,
    isRequired
}) => {
    // Initialize display value from numeric value
    const formatNumberWithCommas = (numValue: number | string): string => {
        if (!numValue || numValue === '') return '';
        // If it's already a string, remove non-digits and format
        if (typeof numValue === 'string') {
            const numbers = numValue.replace(/\D/g, '');
            return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        // If it's a number, convert to string and format
        return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const [displayValue, setDisplayValue] = useState<string>(() => {
        // Initialize from the value prop (which should be numeric)
        if (value !== undefined && value !== null && value !== '') {
            return formatNumberWithCommas(value);
        }
        return '';
    });

    // Update display value when external value changes
    useEffect(() => {
        if (value !== undefined && value !== null && value !== '') {
            const numericVal = typeof value === 'number' ? value : Number(value);
            if (!isNaN(numericVal)) {
                setDisplayValue(formatNumberWithCommas(numericVal));
            }
        } else if (value === '' || value === null || value === undefined) {
            setDisplayValue('');
        }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        // Extract numeric value (remove all non-digit characters)
        const numericOnly = inputValue.replace(/\D/g, '');
        const numeric = numericOnly ? parseInt(numericOnly, 10) : 0;
        
        // Format for display
        const formatted = formatNumberWithCommas(inputValue);
        
        setDisplayValue(formatted);
        
        // Store the numeric value (not the formatted string) in form data
        handleChange(field.name, numeric);
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="flex items-center">
                {renderIcon(field.ui?.icon)}
                <Input
                    id={field.name}
                    type="text"
                    value={displayValue}
                    onChange={handleInputChange}
                    placeholder={field.placeholder || '0'}
                    className={`flex-1 ${hasError ? 'border-destructive' : ''}`}
                />
            </div>
        </div>
    );
};

export default renderNumberInputWithCommas;

